import { useState } from "react";
import { Link } from "react-router-dom";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import BackArrow from "../components/BackArrow.jsx";
import { FolderGrid } from "../components/FolderGrid.jsx";
import { FolderTile } from "../components/FolderTile.jsx";
import { FolderOverlay } from "../components/FolderOverlay.jsx";
import { TOOLS_ACCENT, VIOLET } from "./ToolsShell.jsx";

// V57.7 — ToolsHome restructured from collapsible family sections to
// a 10-folder grid. Tapping a folder opens an overlay showing that
// family's tools. The overlay sits on top of the family grid (rest
// of grid still visible at edges via the semi-transparent backdrop)
// so users keep their spatial context.
//
// Layout:
//   - Portrait phone: 3x3 grid + 10th folder centered on row 4
//   - Landscape / wide: 2 rows of 5
// (Both handled by FolderGrid's CSS media queries.)
//
// Back button closes the overlay (via FolderOverlay's pushState).
// Escape key also closes (keyboard).

const EM = TOOLS_ACCENT;     // red-coral #E0584E
const EM_DEEP = VIOLET;
const EM_LIGHT = "#F5A299";

const rgba = (hex, a) => {
  const r = parseInt(hex.slice(1, 3), 16), g = parseInt(hex.slice(3, 5), 16), b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${a})`;
};

// Tool tile data — each entry has {to, name, desc}. The "novel" flag
// pulls a tool into the Novel & Useful folder as well.
const FAMILIES = [
  {
    id: "level-measure",
    label: "Level & Measure",
    accent: "#A8E0D5",
    accentDeep: "#A33A93",
    tools: [
      { to: "/tools/level",              name: "Level Pro",            desc: "Bubble, 2-axis, angle, slope, plumb, camera modes" },
      { to: "/tools/picture-hanging",    name: "Picture-Hanging Guide", desc: "Plan nail positions for even hanging" },
      { to: "/tools/ruler",              name: "Screen Ruler",          desc: "On-screen ruler, calibrate to a known object" },
      { to: "/tools/object-height",      name: "Object Height",         desc: "Estimate tree or building height from a distance", novel: true },
      { to: "/tools/symmetry",           name: "Symmetry Checker",      desc: "Live mirror overlay to see asymmetry", novel: true },
      { to: "/tools/photo-straightener", name: "Photo Straightener",    desc: "Rotate and crop tilted photos, locally" },
    ],
  },
  {
    id: "visual-camera",
    label: "Visual & Camera",
    accent: "#F2C9A1",
    accentDeep: "#A33A93",
    tools: [
      { to: "/tools/magnifier",       name: "Magnifier",            desc: "High-zoom camera with freeze and torch" },
      { to: "/tools/color-picker",    name: "Color Picker",         desc: "Sample any color as HEX, RGB, HSL" },
      { to: "/tools/brightness",      name: "Brightness Meter",     desc: "Approximate ambient brightness, labeled zones" },
      { to: "/tools/grid-camera",     name: "Grid Overlay Camera",  desc: "Rule-of-thirds, golden ratio, custom grids" },
      { to: "/tools/before-after",    name: "Before / After",       desc: "Slider compare two photos" },
    ],
  },
  {
    id: "sound-audio",
    label: "Sound & Audio",
    accent: "#E6B8EE",
    accentDeep: "#A33A93",
    tools: [
      { to: "/tools/decibel",      name: "Decibel Meter",   desc: "Relative sound level from the mic" },
      { to: "/tools/tone",         name: "Tone Generator",  desc: "Sine, square, triangle waves 20-20kHz" },
      { to: "/tools/tuning-fork",  name: "Tuning Fork",     desc: "A=440, 432, chromatic, guitar tuning" },
      { to: "/tools/metronome",    name: "Metronome",       desc: "40-240 BPM with time signatures and tap tempo" },
      { to: "/tools/spectrogram",  name: "Spectrogram",     desc: "Real-time frequency waterfall display", novel: true },
    ],
  },
  {
    id: "outdoor-nav",
    label: "Outdoor & Navigation",
    accent: "#A8CCEE",
    accentDeep: "#A33A93",
    tools: [
      { to: "/tools/compass",      name: "Compass",       desc: "Heading from device orientation" },
      { to: "/tools/gps",          name: "GPS Coordinates", desc: "Lat/long in decimal and DMS" },
      { to: "/tools/altimeter",    name: "Altimeter",     desc: "GPS-based elevation with baseline tracking" },
      { to: "/tools/sun-moon",     name: "Sun & Moon",    desc: "Sun and moon direction from your location", novel: true },
      { to: "/tools/trail-marker", name: "Trail Marker",  desc: "Mark a spot, get bearing and distance back", novel: true },
    ],
  },
  {
    id: "diy-workshop",
    label: "DIY & Workshop",
    accent: "#F2A797",
    accentDeep: "#A33A93",
    tools: [
      { to: "/tools/metal-detector", name: "Metal Detector",  desc: "Magnetometer-based ferrous metal finder" },
      { to: "/tools/square-checker", name: "Square Checker",  desc: "Test if a corner is exactly 90°" },
      { to: "/tools/drill-bit",      name: "Drill Bit Guide", desc: "Pilot and clearance bits by screw and material" },
      { to: "/tools/roof-pitch",     name: "Roof Pitch",      desc: "Degrees, percent, and rise/12 pitch fraction" },
      { to: "/tools/coverage",       name: "Coverage Calculator", desc: "Bags, gallons, boards for concrete/paint/tile/mulch" },
    ],
  },
  {
    id: "math-engineering",
    label: "Math & Engineering",
    accent: "#F2D69B",
    accentDeep: "#A33A93",
    tools: [
      { to: "/tools/fraction",       name: "Fraction Calculator",  desc: "Mixed number add/subtract/multiply/divide" },
      { to: "/tools/right-triangle", name: "Right Triangle",       desc: "Any 2 of 6 properties solves the rest" },
      { to: "/tools/resistor",       name: "Resistor Color Code",  desc: "Color bands to value, and value to bands" },
      { to: "/tools/torque",         name: "Bolt Torque",          desc: "Tightening torque by grade and size" },
    ],
  },
  {
    id: "time-counting",
    label: "Time & Counting",
    accent: "#EDB3CD",
    accentDeep: "#A33A93",
    tools: [
      { to: "/tools/interval", name: "Interval Timer",  desc: "Sequenced work/rest intervals with audio cues" },
      { to: "/tools/tally",    name: "Tally Counter",   desc: "Multiple named counters with CSV export", novel: true },
      { to: "/tools/reaction", name: "Reaction Timer",  desc: "Reflex test — release when the color flips", novel: true },
    ],
  },
  {
    id: "sensor-lab",
    label: "Sensor Lab",
    accent: "#B3DBA3",
    accentDeep: "#A33A93",
    tools: [
      { to: "/tools/vibration",      name: "Vibration Meter",  desc: "Real-time accelerometer waveform", novel: true },
      { to: "/tools/magnetic-field", name: "Magnetic Field",   desc: "Magnetometer direction + magnitude", novel: true },
      { to: "/tools/steadiness",     name: "Hand Steadiness",  desc: "10-second stillness test, scored", novel: true },
      { to: "/tools/shake-counter",  name: "Shake / Rotation", desc: "Counts shakes and full rotations" },
    ],
  },
  {
    id: "practical",
    label: "Practical & Generators",
    accent: "#C9BDED",
    accentDeep: "#A33A93",
    tools: [
      { to: "/tools/converter", name: "Unit Converter",      desc: "Length, mass, volume, speed, area, data, temp" },
      { to: "/tools/qr",        name: "QR Code",             desc: "Generate a QR code from any text or link" },
      { to: "/tools/currency",  name: "Currency Converter",  desc: "24 currencies, honest about staleness" },
      { to: "/tools/password",  name: "Password Generator",  desc: "Crypto-random with entropy bits shown" },
      { to: "/tools/exif",      name: "EXIF Remover",        desc: "Strip photo metadata locally, see what was found", novel: true },
    ],
  },
];

// Build the 10th folder: Novel & Useful — flat list of tools marked novel.
const NOVEL_TOOLS = FAMILIES.flatMap((f) => f.tools.filter((t) => t.novel));
const NOVEL_FOLDER = {
  id: "novel-useful",
  label: "Novel & Useful",
  accent: "#FCDFA1",
  accentDeep: "#A33A93",
  tools: NOVEL_TOOLS,
};

// 10 folders total — Novel & Useful as the bottom-centered 10th tile.
const ALL_FOLDERS = [...FAMILIES, NOVEL_FOLDER];

// ToolsLanding — small card view at /tools (deferred-route landing).
export function ToolsLanding() {
  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", color: "#E8E9F3", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24, textAlign: "center", paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}>
      <div style={{ width: 72, height: 72, borderRadius: 18, background: `linear-gradient(135deg, ${TOOLS_ACCENT}, ${VIOLET})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, fontWeight: 700, color: "#fff", marginBottom: 20 }}>
        T
      </div>
      <h1 style={{ fontSize: 26, fontWeight: 700, margin: "0 0 8px" }}>NerdTools</h1>
      <p style={{ fontSize: 15, color: "#9A9DBE", margin: "0 0 28px", maxWidth: 320 }}>
        Your phone's sensors and calculators, no app install. Free. Fast. Actually good.
      </p>
      <Link to="/tools/home" style={{ background: TOOLS_ACCENT, color: "#1A0604", fontWeight: 600, fontSize: 15, padding: "13px 28px", borderRadius: 12, textDecoration: "none" }}>
        Open NerdTools
      </Link>
    </div>
  );
}

export function ToolsHome() {
  const [openFamily, setOpenFamily] = useState(null);

  // Looked up from the list by id when overlay is opened
  const activeFamily = openFamily ? ALL_FOLDERS.find((f) => f.id === openFamily) : null;

  return (
    <div style={{ minHeight: "100vh", background: "#0A0B14", fontFamily: "'DM Sans', system-ui, sans-serif", color: "#E2E8F0" }}>
      <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, height: "70%", background: `radial-gradient(ellipse at center bottom, ${rgba(EM, 0.18)} 0%, transparent 70%)`, pointerEvents: "none" }} />

      <header style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "16px 24px",
        paddingTop: "calc(16px + env(safe-area-inset-top))",
        paddingLeft: "calc(24px + env(safe-area-inset-left))",
        paddingRight: "calc(24px + env(safe-area-inset-right))",
        position: "relative", zIndex: 10,
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <BackArrow to="/" from={EM} toColor={EM_LIGHT} />
          <div style={{ width: 38, height: 38, borderRadius: 11, background: `linear-gradient(135deg, ${EM}, ${EM_DEEP})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 17, fontWeight: 800, color: "#fff" }}>T</div>
          <svg width="110" height="24" viewBox="0 0 110 24" xmlns="http://www.w3.org/2000/svg" style={{ display: "block" }}>
            <defs>
              <linearGradient id="wm-tools" x1="0" y1="0" x2="1" y2="1">
                <stop offset="0" stopColor={EM_LIGHT} />
                <stop offset="1" stopColor="#F5A299" />
              </linearGradient>
            </defs>
            <text x="0" y="19" fill="url(#wm-tools)" fontFamily="DM Sans, system-ui, sans-serif" fontSize="20" fontWeight="800" letterSpacing="-0.5">NerdTools</text>
          </svg>
        </div>
      </header>

      <main style={{ maxWidth: 920, margin: "0 auto", padding: "40px 20px 80px", position: "relative", zIndex: 10 }}>
        <h1 style={{ fontSize: "clamp(32px, 6vw, 52px)", fontWeight: 800, letterSpacing: "-1.5px", textAlign: "center", margin: "0 0 14px", lineHeight: 1.1 }}>
          43 tools.<br /><span style={{ background: `linear-gradient(135deg, ${EM_LIGHT}, ${EM})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Free. Fast. Actually good.</span>
        </h1>
        <p style={{ fontSize: 17, color: "#9CA3AF", textAlign: "center", maxWidth: 580, margin: "0 auto 14px", lineHeight: 1.6 }}>
          Your phone's sensors and calculators as a toolkit. Bubble level, magnifier, spectrogram, EXIF remover, password generator — everything you'd want to install separately, here without an install.
        </p>
        <p style={{ fontSize: 15, color: "#6F7290", textAlign: "center", maxWidth: 580, margin: "0 auto 36px", lineHeight: 1.6 }}>
          No tracking. No signup. No dark patterns.
        </p>

        <FolderGrid ariaLabel="Tool family folders">
          {ALL_FOLDERS.map((f) => (
            <FolderTile
              key={f.id}
              family={{ ...f, toolCount: f.tools.length }}
              onClick={() => setOpenFamily(f.id)}
            />
          ))}
        </FolderGrid>
      </main>

      <FolderOverlay
        family={activeFamily}
        onClose={() => setOpenFamily(null)}
      />

      <footer style={{ textAlign: "center", padding: 24, fontSize: 12, color: "#1F2937", borderTop: `1px solid ${rgba(EM, 0.04)}` }}>
        <NerdStackFooter variant="tool" family="tools" />
      </footer>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { margin: 0; }
      `}</style>
    </div>
  );
}
